/** The signed-in user's own profile -- what the profile screens show and edit. */
export interface UserProfile {
  readonly id: string;
  readonly username: string;
  readonly email: string;
  readonly nameEn: string;
  readonly nameAr: string;
  /** International format, e.g. "+966583451111"; "" when there is none. */
  readonly phoneNumber: string;
  /** null for the tenant's own staff, who belong to no company. */
  readonly company: { readonly nameEn: string; readonly nameAr: string } | null;
  readonly twoFactorEnabled: boolean;
  /** ISO timestamp; null when the password was never changed. */
  readonly lastPasswordChangedAt: string | null;
  /** null shows the default avatar. */
  readonly avatarUrl: string | null;
}

/** What Save does to the picture: leave it, swap it for a new file, or drop it. */
export type ProfileAvatarChange =
  | { readonly kind: "keep" }
  | { readonly kind: "replace"; readonly file: File }
  | { readonly kind: "remove" };

/** The editable fields (username, email and company are read-only). */
export interface ProfileUpdateInput {
  readonly nameEn: string;
  readonly nameAr: string;
  readonly phoneNumber: string;
  readonly avatar: ProfileAvatarChange;
}

export interface ChangePasswordInput {
  readonly currentPassword: string;
  readonly newPassword: string;
  readonly confirmPassword: string;
}

/**
 * Every other session ends on a password change. The current one survives
 * with a fresh access token (the one the request carried is revoked) --
 * unless the server couldn't tell which session it was, when it ends too.
 */
export type ChangePasswordResult =
  | { readonly session: "kept"; readonly accessToken?: string }
  | { readonly session: "ended" };

export type ProfileErrorReason =
  | "incorrectCurrentPassword"
  | "sameAsCurrentPassword"
  /** Any 400 from the API; the error's message is the backend's own copy. */
  | "passwordRejected"
  | "invalidVerificationCode"
  | "verificationCodeExpired"
  /** Too many wrong guesses: that code is dead, even typed correctly. */
  | "verificationAttemptsExceeded";

/**
 * A rejection the screens answer with their own inline copy rather than the
 * generic error toast.
 */
export class ProfileRequestError extends Error {
  readonly reason: ProfileErrorReason;

  constructor(reason: ProfileErrorReason, message: string = reason) {
    super(message);
    this.name = "ProfileRequestError";
    this.reason = reason;
  }
}

export function isProfileRequestError(
  error: unknown,
  reason?: ProfileErrorReason,
): error is ProfileRequestError {
  return (
    error instanceof ProfileRequestError &&
    (reason === undefined || error.reason === reason)
  );
}
